import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Phone, 
  Mail, 
  MapPin, 
  ArrowLeft,
  MessageCircle,
  Clock,
  Globe
} from 'lucide-react';
import { Link } from 'react-router-dom';

const Contact = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-pink-50 to-red-50">
      {/* Navigation Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-pink-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_legaltech-ghana/artifacts/2u2cnqb9_file_000000007aec61f8982bd709fec16f87%20%281%29.png"
                alt="EduLegalHub Logo"
                className="h-10 w-auto"
              />
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-pink-600 bg-clip-text text-transparent">
                EduLegalHub
              </span>
            </Link>

            {/* Navigation */}
            <nav className="flex items-center space-x-8">
              <Link to="/" className="text-gray-700 hover:text-blue-600 transition-colors font-medium">
                <ArrowLeft className="h-4 w-4 mr-2 inline" />
                Back to Home
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto text-center">
          <div className="max-w-4xl mx-auto">
            <Badge className="bg-gradient-to-r from-blue-100 to-pink-100 text-blue-700 border-blue-200 px-4 py-2 mb-6">
              Get in Touch
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-blue-600 to-pink-600 bg-clip-text text-transparent">
                Contact Us
              </span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-12">
              We're here to help you with any questions about our legal document generation 
              and educational management services. Reach out to us anytime.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="pb-20 px-4">
        <div className="container mx-auto">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12">
              
              {/* Contact Details */}
              <div className="space-y-8">
                <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-white">
                  <CardHeader>
                    <CardTitle className="flex items-center text-blue-800">
                      <Phone className="h-6 w-6 mr-3" />
                      Phone Support
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-700 mb-4">
                      Call us directly for immediate assistance with our services
                    </CardDescription>
                    <div className="space-y-2">
                      <div className="flex items-center text-lg font-semibold text-blue-600">
                        <Phone className="h-5 w-5 mr-2" />
                        <a href="tel:+233243666428" className="hover:underline">
                          +233 24 366 6428
                        </a>
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Clock className="h-4 w-4 mr-2" />
                        Monday - Friday: 8:00 AM - 6:00 PM
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg bg-gradient-to-br from-green-50 to-white">
                  <CardHeader>
                    <CardTitle className="flex items-center text-green-800">
                      <MessageCircle className="h-6 w-6 mr-3" />
                      WhatsApp Support
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-700 mb-4">
                      Chat with us instantly on WhatsApp for quick responses
                    </CardDescription>
                    <div className="space-y-2">
                      <a 
                        href="https://wa.me/233243666428" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center text-lg font-semibold text-green-600 hover:text-green-700 transition-colors"
                      >
                        <MessageCircle className="h-5 w-5 mr-2" />
                        +233 24 366 6428
                      </a>
                      <div className="flex items-center text-sm text-gray-600">
                        <Clock className="h-4 w-4 mr-2" />
                        Quick response guaranteed
                      </div>
                    </div>
                    <a 
                      href="https://wa.me/233243666428" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="mt-4 inline-flex items-center px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
                    >
                      <MessageCircle className="h-4 w-4 mr-2" />
                      Start Chat
                    </a>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-50 to-white">
                  <CardHeader>
                    <CardTitle className="flex items-center text-purple-800">
                      <MapPin className="h-6 w-6 mr-3" />
                      Our Location
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-700 mb-4">
                      Visit our office for in-person consultations
                    </CardDescription>
                    <div className="space-y-2">
                      <div className="flex items-start text-lg font-semibold text-purple-600">
                        <MapPin className="h-5 w-5 mr-2 mt-0.5" />
                        <div>
                          <div>Accra, Ghana</div>
                          <div className="text-sm text-gray-600 font-normal">
                            Greater Accra Region
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Globe className="h-4 w-4 mr-2" />
                        Serving clients across Ghana
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Services Overview */}
              <div className="space-y-8">
                <Card className="border-0 shadow-lg bg-gradient-to-br from-gray-50 to-white">
                  <CardHeader>
                    <CardTitle className="text-gray-800">How Can We Help You?</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      <div>
                        <h4 className="font-semibold text-blue-800 mb-2">Legal Document Generation</h4>
                        <p className="text-gray-600 text-sm">
                          Get assistance with creating court-compliant legal documents using our AI-powered platform. 
                          We support various document types including affidavits, power of attorney, and court applications.
                        </p>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold text-pink-800 mb-2">Educational Management</h4>
                        <p className="text-gray-600 text-sm">
                          Need help with our lesson notes system? We provide support for teachers, headteachers, 
                          and educational administrators using our comprehensive educational management platform.
                        </p>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold text-green-800 mb-2">Technical Support</h4>
                        <p className="text-gray-600 text-sm">
                          Experience any technical issues? Our support team is ready to help you navigate 
                          our platform and resolve any technical challenges you may encounter.
                        </p>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold text-purple-800 mb-2">Account & Billing</h4>
                        <p className="text-gray-600 text-sm">
                          Questions about subscriptions, payments, or account management? 
                          We're here to help with all billing and account-related inquiries.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg bg-gradient-to-r from-blue-600 to-pink-600 text-white">
                  <CardHeader>
                    <CardTitle className="text-white">Quick Actions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <Button 
                        size="lg" 
                        className="w-full bg-white text-blue-600 hover:bg-gray-100 font-semibold"
                        asChild
                      >
                        <Link to="/document-generator">
                          Generate Legal Documents
                        </Link>
                      </Button>
                      
                      <Button 
                        size="lg" 
                        variant="outline"
                        className="w-full border-2 border-white text-white hover:bg-white hover:text-blue-600 font-semibold"
                        asChild
                      >
                        <Link to="/knowledge-base">
                          Browse Knowledge Base
                        </Link>
                      </Button>

                      <Button 
                        size="lg" 
                        variant="outline"
                        className="w-full border-2 border-white text-white hover:bg-white hover:text-blue-600 font-semibold"
                        asChild
                      >
                        <Link to="/">
                          Get Started with Lesson Notes
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="mt-16">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4 text-gray-800">Frequently Asked Questions</h2>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  Find quick answers to common questions about our services
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">How do I generate a legal document?</h4>
                    <p className="text-gray-600 text-sm">
                      Simply click on "Generate Legal Documents", select your document type, fill out the form, 
                      make payment, and receive your professionally formatted document.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Are the documents court-compliant?</h4>
                    <p className="text-gray-600 text-sm">
                      Yes, all our documents are generated according to Ghanaian court standards and 
                      formatting requirements, ensuring they meet legal compliance.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">How does teacher subscription work?</h4>
                    <p className="text-gray-600 text-sm">
                      Teachers can subscribe to access weekly lesson notes, download resources, 
                      and upload content to their headteacher's dashboard for review and approval.
                    </p>
                  </div>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">What payment methods do you accept?</h4>
                    <p className="text-gray-600 text-sm">
                      We accept various payment methods through Paystack, including mobile money, 
                      bank transfers, and debit/credit cards.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Can I get a refund?</h4>
                    <p className="text-gray-600 text-sm">
                      Refund policies vary by service. Please contact our support team to discuss 
                      your specific situation and we'll help resolve any issues.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Do you provide training for schools?</h4>
                    <p className="text-gray-600 text-sm">
                      Yes, we offer training sessions for educational institutions to help teachers 
                      and administrators make the most of our platform.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;